import type { ReactNode } from 'react';
import { Blueprint } from '@/components/art/blueprint';
import { BlueprintMobile } from '@/components/art/blueprint-mobile';

// Shared page header: the homepage hero's shape on every page. Eyebrow, headline, one
// line of copy, the Blueprint art bleeding off the top-right on desktop and its own
// mobile band below the copy. Nothing ever sits under the copy. Full-bleed; render it
// above PageContainer, not inside it.
interface PageHeroProps {
  eyebrow: string;
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode; // optional extra copy or actions under the subtitle
  /** optional brand mark (the canonical logo with personality) shown above the eyebrow */
  mark?: { src: string; alt: string };
  mobileArt?: boolean; // false drops the phone art (Contact: keep the calendar on the first screen)
}

export function PageHero({
  eyebrow,
  title,
  subtitle,
  children,
  mark,
  mobileArt = true,
}: PageHeroProps) {
  return (
    <section className="hero-section relative overflow-hidden bg-[#0a0a0b]">
      <Blueprint className="hidden lg:block" />
      <div className="max-w-7xl mx-auto px-4 pt-14 sm:pt-20 md:pt-24 pb-10 md:pb-14 relative z-10">
        <div className="max-w-4xl lg:max-w-[800px]">
          {mark && (
            <img
              src={mark.src}
              alt={mark.alt}
              className="h-28 md:h-40 w-auto mb-6 md:mb-8"
              loading="eager"
            />
          )}
          <p className="text-white/55 font-medium uppercase text-xs tracking-[0.3em] mb-5">
            {eyebrow}
          </p>
          <h1
            className="text-[2.4rem] sm:text-5xl md:text-6xl font-extrabold text-white mb-4 md:mb-5 leading-[1.02]"
            style={{ letterSpacing: '-0.03em' }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-base md:text-xl text-white/70 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          )}
          {children}
        </div>
        {mobileArt && <BlueprintMobile className="lg:hidden mt-10 max-w-sm" />}
      </div>
    </section>
  );
}
